import { useEffect, useRef, useCallback } from 'react'
// hls.js (~500 KB) loads dynamically when an HLS video actually renders —
// a static import here pulled it into the initial bundle via the dialog
// presets the landing page mounts (route-splitting 2026-07-03).
import type HlsType from 'hls.js'

const STORAGE_BASE = 'https://api-storage.arkturian.com/storage/media'

interface VerlaufMediaProps {
  storageId: number
  /** Optional HLS stream URL. If provided, uses HLS adaptive streaming. */
  hlsUrl?: string
  /** Set to true to play the storage object as a direct MP4 video. */
  video?: boolean
}

/**
 * Media slot for VerlaufDialog — renders video or image from a Storage API ID.
 *
 * Usage:
 *   <VerlaufMedia storageId={102938} />                          // image only
 *   <VerlaufMedia storageId={101705} video />                    // direct MP4 video
 *   <VerlaufMedia storageId={102938} hlsUrl="https://..." />     // HLS video
 */
export default function VerlaufMedia({ storageId, hlsUrl, video }: VerlaufMediaProps) {
  const mediaUrl = `${STORAGE_BASE}/${storageId}`
  const posterUrl = `${mediaUrl}?variant=medium&format=jpg`

  if (hlsUrl) {
    return <HlsVideo hlsUrl={hlsUrl} posterUrl={posterUrl} />
  }

  if (video) {
    return (
      <video
        className="sg-vdialog__video"
        src={mediaUrl}
        muted
        loop
        playsInline
        autoPlay
        poster={posterUrl}
      />
    )
  }

  return (
    <img
      className="sg-vdialog__video"
      src={posterUrl}
      alt=""
      style={{ objectFit: 'cover' }}
    />
  )
}

function HlsVideo({ hlsUrl, posterUrl }: { hlsUrl: string; posterUrl: string }) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    let hls: HlsType | null = null
    let cancelled = false

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS (Safari/iOS) — no library needed.
      video.src = hlsUrl
      video.play().catch(() => {})
    } else {
      void import('hls.js').then(({ default: HlsCtor }) => {
        if (cancelled || !HlsCtor.isSupported()) return
        hls = new HlsCtor({ startLevel: -1 })
        hls.loadSource(hlsUrl)
        hls.attachMedia(video)
        hls.on(HlsCtor.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}) })
      })
    }

    return () => { cancelled = true; hls?.destroy() }
  }, [hlsUrl])

  return (
    <video
      ref={videoRef}
      className="sg-vdialog__video"
      muted
      loop
      playsInline
      poster={posterUrl}
    />
  )
}
